import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import { TawkGate } from "@/components/TawkGate";
import Login from "@/pages/Login";
import Trade from "@/pages/Trade";
import Positions from "@/pages/Positions";
import HistoryPage from "@/pages/History";
import Admin from "@/pages/Admin";
import Pods from "@/pages/Pods";
import Leads from "@/pages/Leads";
import Marketing from "@/pages/Landing";
import TraderDetail from "@/pages/TraderDetail";
import TradingLogic from "@/pages/TradingLogic";
import { About, Contact, Privacy, RefundPolicy, RiskDisclosure, Terms } from "@/pages/Legal";

const Protected = ({ children, adminOnly, traderOnly }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#08090A]" />;
  if (!user) return <Navigate to="/" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/trade" replace />;
  if (traderOnly && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
};

// Traders and admins both sign in at /login on the same host — no separate admin subdomain.
const Home = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#08090A]" />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/trade"} replace />;
  return <Marketing />;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <TawkGate />
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/home" element={<Marketing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/risk-disclosure" element={<RiskDisclosure />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route
        path="/admin/leads"
        element={
          <Protected adminOnly>
            <Leads />
          </Protected>
        }
      />
      <Route
        path="/trade"
        element={
          <Protected traderOnly>
            <Trade />
          </Protected>
        }
      />
      <Route
        path="/positions"
        element={
          <Protected traderOnly>
            <Positions />
          </Protected>
        }
      />
      <Route path="/limit" element={<Navigate to="/trade" replace />} />
      <Route path="/orders" element={<Navigate to="/positions" replace />} />
      <Route path="/dashboard" element={<Navigate to="/trade" replace />} />
      <Route
        path="/history"
        element={
          <Protected traderOnly>
            <HistoryPage />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected adminOnly>
            <Admin />
          </Protected>
        }
      />
      <Route
        path="/admin/pods"
        element={
          <Protected adminOnly>
            <Pods />
          </Protected>
        }
      />
      <Route
        path="/admin/logic"
        element={
          <Protected adminOnly>
            <TradingLogic />
          </Protected>
        }
      />
      <Route
        path="/admin/traders/:userId"
        element={
          <Protected adminOnly>
            <TraderDetail />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
